import { isNetworkPort } from '../derive/network-list.js';
import { defaultLayerKeyFor } from '../model/layers.js';
import { splitParentKey } from '../model/layerSplit.js';
import type {
  BoardData,
  BuiltinLayerKey,
  Channel,
  CollectionName,
  Connection,
  FreeformElement,
  Item,
  Mix,
} from '../model/schema.js';
import { findPort } from '../trace/ports.js';
import type { AnchorLike, CatchupTable, Topic } from './types.js';

/** Topic of a built-in layer: the notes and ink layers (and custom layers) count as general. */
export function topicOfLayerKey(key: BuiltinLayerKey | undefined): Topic {
  if (key === undefined || key === 'notes' || key === 'ink') return 'general';
  return splitParentKey(key) as Topic;
}

function itemTopic(data: BoardData, item: Item): Topic {
  const key = data.layers[item.layerId]?.key;
  return topicOfLayerKey(key ?? defaultLayerKeyFor(item.kind));
}

function connectionTopic(data: BoardData, c: Connection): Topic {
  const a = findPort(data, c.from.itemId, c.from.portId);
  const b = findPort(data, c.to.itemId, c.to.portId);
  if (a?.signal === 'power' || b?.signal === 'power') return 'power';
  if (isNetworkPort(a) || isNetworkPort(b)) return 'network';
  return 'cabling';
}

function channelTopic(data: BoardData, ch: Channel): Topic {
  const source = ch.sourceItemId === undefined ? undefined : data.items[ch.sourceItemId];
  return source === undefined ? 'microphones' : itemTopic(data, source);
}

function freeformTopic(data: BoardData, el: FreeformElement): Topic {
  const layer = el.layerId === undefined ? undefined : data.layers[el.layerId];
  return topicOfLayerKey(layer?.key);
}

/** Area (decision O5) of an entity, derived from its layer or kind. */
export function topicOf(
  data: BoardData,
  collection: CollectionName | 'meta',
  entity: unknown,
): Topic {
  switch (collection) {
    case 'items':
      return itemTopic(data, entity as Item);
    case 'freeform':
      return freeformTopic(data, entity as FreeformElement);
    case 'connections':
      return connectionTopic(data, entity as Connection);
    case 'channels':
      return channelTopic(data, entity as Channel);
    case 'mixes': {
      const kind = (entity as Mix).kind;
      return kind === 'monitor' || kind === 'iem' ? 'monitors' : 'pa';
    }
    case 'power':
      return 'power';
    case 'frames':
      return 'stage';
    default:
      return 'general';
  }
}

const ROW_TABLE_TOPICS: Readonly<Record<string, Topic>> = {
  input: 'microphones',
  patch: 'stageboxes',
  monitors: 'monitors',
  power: 'power',
  network: 'network',
};

/** Topic of a comment / open item anchor. */
export function topicOfAnchor(data: BoardData, anchor: AnchorLike | null): Topic {
  if (anchor === null) return 'general';
  if (anchor.type === 'item') {
    const item = data.items[anchor.itemId];
    return item === undefined ? 'general' : itemTopic(data, item);
  }
  if (anchor.type === 'row' && Object.hasOwn(ROW_TABLE_TOPICS, anchor.table))
    return ROW_TABLE_TOPICS[anchor.table]!;
  return 'general';
}

const TABLE_TOPICS: Readonly<Record<CatchupTable, Topic>> = {
  input: 'microphones',
  monitors: 'monitors',
  patch: 'stageboxes',
  'power-loads': 'power',
  'power-sources': 'power',
  network: 'network',
  stands: 'microphones',
};

/** Topic of a derived table. */
export function topicOfTable(table: CatchupTable): Topic {
  return TABLE_TOPICS[table];
}
